import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { ArrowRight, Briefcase } from "lucide-react";
import { Button } from "@/components/ui/button";
import { projects } from "@/data/projects";
import { localizeProjects } from "@/lib/localizeProjects";
import { useSiteLanguage } from "@/lib/useSiteLanguage";
import { servicesWorkSectionCopy } from "@/lib/siteCopy";

export function ServicesWorkSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const navigate = useNavigate();
  const lang = useSiteLanguage();
  const t = servicesWorkSectionCopy[lang];
  const preview = localizeProjects(projects, lang).slice(0, 3);

  return (
    <section
      ref={ref}
      className="py-16 md:py-20 border-t border-border/60 bg-muted/20"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="max-w-5xl mx-auto"
        >
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
            <div>
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">
                <Briefcase className="w-3.5 h-3.5" />
                {t.eyebrow}
              </div>
              <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-foreground">
                {t.title}
              </h2>
              <p className="text-sm md:text-base text-muted-foreground mt-2 max-w-lg">
                {t.subtitle}
              </p>
            </div>
            <Button
              className="rounded-full shrink-0 self-start md:self-auto"
              onClick={() => navigate("/work")}
            >
              {t.viewAll}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            {preview.map((project, index) => (
              <motion.button
                key={project.id}
                type="button"
                initial={{ opacity: 0, y: 12 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: index * 0.08 }}
                onClick={() => navigate(`/work/${project.id}`)}
                className="group text-left rounded-2xl border border-border/70 bg-card overflow-hidden hover:border-primary/25 hover:shadow-md transition-all duration-300"
              >
                <div className="aspect-[16/10] overflow-hidden bg-muted">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <div className="p-4">
                  <p className="text-xs text-muted-foreground mb-1">
                    {project.category}
                  </p>
                  <h3 className="font-medium text-sm text-foreground line-clamp-2 group-hover:text-primary transition-colors">
                    {project.title}
                  </h3>
                </div>
              </motion.button>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
